import React from 'react';
import PropTypes from 'prop-types';
import { ResponsiveBar } from 'cccisd-nivo/bar';

const colorMap = {
    student: ['#2172b5', '#073351', '#68c1f2'],
    educator: ['#594ba8', '#2b2659', '#a492ef'],
    provider: ['#d14620', '#771104', '#ff8e66'],
    caregiver: ['#308b86', '#093331', '#5bc1be'],
};

const fill = [
    {
        id: 'rightDiagonal',
        type: 'patternLines',
        background: 'inherit',
        color: 'rgba(255, 255, 255, 0.75)',
        rotation: 45,
        lineWidth: 1,
        spacing: 5,
    },
    {
        id: 'horizontal',
        type: 'patternLines',
        background: 'inherit',
        color: 'rgba(255, 255, 255, 0.75)',
        rotation: 0,
        lineWidth: 1,
        spacing: 7,
    },
    {
        id: 'leftDiagonal',
        type: 'patternLines',
        background: 'inherit',
        color: 'rgba(255, 255, 255, 0.75)',
        rotation: -45,
        lineWidth: 1,
        spacing: 5,
    },
    {
        id: 'vertical',
        type: 'patternLines',
        background: 'inherit',
        color: 'rgba(255, 255, 255, 0.75)',
        rotation: 90,
        lineWidth: 1,
        spacing: 7,
    },
];

const divideArray = (arr, num) => {
    const result = [];
    const chunkSize = Math.ceil(arr.length / num);
    for (let i = 0; i < num; i++) {
        result[i] = arr.slice(chunkSize * i, chunkSize * (i + 1));
    }
    return result;
};

const Chart = props => {
    const { page, isPreview, settings, params, data } = props;
    const { formulas, markers, min, max } = page;
    const keys = formulas.map((_, index) => `var${index + 1}`);
    const userGroup = isPreview ? settings.previewUserGroup || 'student' : params.contactType;

    const getColor = index => {
        const colorIndex = index % colorMap[userGroup].length;
        return colorMap[userGroup][colorIndex];
    };
    const getFill = index => {
        const color = getColor(index);

        const fillTotal = fill.length + 1;
        const fillIndex = (Math.floor(index / colorMap[userGroup].length) % fillTotal) - 1;
        return fillIndex >= 0 ? `url(#${fill[fillIndex].id}.bg.${color})` : 'transparent';
    };

    const { legendWidth, legendLabels } = (() => {
        const longestLegend = Math.max(...formulas.map(item => item.label.length));
        const letterSize = 130 / 26;
        const _legendWidth = letterSize * longestLegend + 50;
        const legendTotalRows = Math.ceil(formulas.length / Math.floor(700 / _legendWidth));

        const labels = formulas.map((item, index) => ({
            id: `var${index + 1}`,
            label: item.label,
            color: getColor(index),
        }));

        return {
            legendWidth: _legendWidth,
            legendLabels: divideArray(labels, legendTotalRows),
        };
    })();

    if (formulas.length === 0) {
        return null;
    }

    const width = 770;
    const height = 380 + legendLabels.length * 20;

    if (!data) {
        return (
            <div className="p-4 border" style={{ width: `${width}px`, height: `${height}px` }}>
                Loading chart data...
            </div>
        );
    }
    if (data.length === 0) {
        return (
            <div className="p-4 border" style={{ width: `${width}px`, height: `${height}px` }}>
                There are no timepoints for the chart.
            </div>
        );
    }

    const MinHeightBarsLayer = ({ bars }) => {
        const minHeight = 3; // px
        return (
            <>
                {bars.map(bar => {
                    const isNull = !Number.isFinite(bar.data.value);
                    // eslint-disable-next-line no-nested-ternary
                    const barHeight = isNull ? 0 : bar.height === 0 ? minHeight : bar.height;
                    const y = bar.height === 0 ? bar.y - minHeight : bar.y;

                    const maxWidth = 200;
                    const adjustedWidth = Math.min(bar.width, maxWidth);
                    const offset = (bar.width - adjustedWidth) / 2;

                    return (
                        <rect
                            key={bar.key}
                            x={bar.x + offset}
                            y={y}
                            width={adjustedWidth}
                            height={barHeight}
                            fill={bar.data.fill || bar.color}
                        />
                    );
                })}
            </>
        );
    };

    const CustomLabelsLayer = ({ bars }) => (
        <>
            {bars.map(bar => {
                const tooSmall = bar.height < 20; // threshold in px for "too small" bar
                const x = bar.x + bar.width / 2;
                const y = tooSmall
                    ? bar.y - 10 // put above the bar
                    : bar.y + bar.height / 2; // default inside

                if (!Number.isFinite(bar.data.value)) {
                    return null;
                }

                return (
                    <text
                        key={bar.key}
                        x={x}
                        y={y}
                        textAnchor="middle"
                        dominantBaseline={tooSmall ? 'baseline' : 'middle'}
                        style={{
                            fill: tooSmall ? '#000' : '#fff',
                            fontSize: 12,
                        }}
                        data-chart-bar-value
                    >
                        {bar.data.value}
                    </text>
                );
            })}
        </>
    );

    return (
        <div style={{ width: `${width}px`, height: `${height}px` }}>
            <ResponsiveBar
                data={data}
                keys={keys}
                indexBy="timepoint"
                groupMode="grouped"
                tooltip={() => null}
                colors={({ id }) => {
                    const index = Number(id.slice(3)) - 1;
                    return getColor(index);
                }}
                margin={{
                    top: 6,
                    right: 0,
                    bottom: 40 + legendLabels.length * 20,
                    left: page.yAxisLabel ? 50 : 40,
                }}
                innerPadding={2}
                axisBottom={{
                    format: value => value.slice(0, 10), // Leave just date
                }}
                legends={legendLabels.map((labels, index1) => ({
                    anchor: 'bottom-left',
                    data: labels,
                    direction: 'row',
                    translateY: 60 + index1 * 20,
                    translateX: (width - legendLabels[0].length * legendWidth) / 2,
                    itemsSpacing: 3,
                    itemWidth: legendWidth,
                    itemHeight: 16,
                    symbolShape: item => {
                        const index = Number(item.id.slice(3)) - 1;

                        return (
                            <g>
                                <rect width={item.size} height={item.size} fill={item.fill} />
                                <rect width={item.size} height={item.size} fill={getFill(index)} />
                            </g>
                        );
                    },
                }))}
                {...(page.yAxisLabel
                    ? { axisLeft: { legend: page.yAxisLabel, legendPosition: 'middle', legendOffset: -44 } }
                    : {})}
                {...(typeof min === 'number' ? { minValue: min } : {})}
                {...(typeof max === 'number' ? { maxValue: max } : {})}
                {...(markers.length > 0
                    ? {
                          markers: markers.map(item => ({
                              axis: 'y',
                              value: item.value,
                              lineStyle: { stroke: '#000', strokeWidth: 2, strokeDasharray: '10 10', opacity: 0.25 },
                              legend: item.label,
                              legendPosition: 'top-right',
                              textStyle: {
                                  fill: '#000',
                                  fontWeight: 'bold',
                                  opacity: 0.5,
                                  textShadow:
                                      '1px 1px 1px #fff,1px -1px 1px #fff, -1px 1px 1px #fff,-1px -1px 1px #fff,1px 0 1px #fff,-1px 0 1px #fff, 0 1px 1px #fff,0 -1px 1px #fff',
                              },
                          })),
                      }
                    : {})}
                fill={fill.map((item, index) => ({
                    match: d => {
                        const varIndex = Number(d.data.id.slice(3)) - 1;
                        const fillTotal = fill.length + 1;
                        const fillIndex = Math.floor(varIndex / colorMap[userGroup].length) % fillTotal;
                        return index === fillIndex - 1;
                    },
                    id: item.id,
                }))}
                defs={fill}
                labelSkipHeight={99999}
                layers={['grid', 'axes', MinHeightBarsLayer, CustomLabelsLayer, 'markers', 'legends']}
            />
        </div>
    );
};

Chart.propTypes = {
    page: PropTypes.object,
    isPreview: PropTypes.bool,
    settings: PropTypes.object,
    params: PropTypes.object,
    data: PropTypes.array,
};

export default Chart;
